import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, MenuItem, Paper, TextField,
  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, CircularProgress, Collapse, Select, FormControl, InputLabel,
  Checkbox, ListItemText, OutlinedInput, Autocomplete, Menu, Divider
} from '@mui/material';
import {
  Receipt, Search, Refresh, PictureAsPdf, Code, Archive,
  FilterList, Clear, TableChart
} from '@mui/icons-material';
import axios from 'axios';
import { exportGenericWorkbook } from '../utils/excel/genericWorkbook';
import { useResizableCols } from '../hooks/useResizableCols';

// ─── Drag handle para resize de columnas ─────────────────────────────────────
const ResizeHandle = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(25,118,210,0.7)' : 'rgba(0,0,0,0.22)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

// ─── Constantes ───────────────────────────────────────────────────────────────

const TIPO_OPTIONS = [
  { value: '01',         label: 'Facturas' },
  { value: '03',         label: 'Boletas' },
  { value: '07',         label: 'Notas de Crédito' },
  { value: '08',         label: 'Notas de Débito' },
  { value: 'cotizacion', label: 'Cotizaciones' },
  { value: 'nota_venta', label: 'Notas de Venta' },
  { value: 'guia',       label: 'Guías de Remisión' },
];

const ESTADO_OPTIONS = [
  { value: 'ACEPTADO', label: '✓ Aceptado' },
  { value: 'ENVIADO',  label: '📤 Enviado a SUNAT' },
  { value: 'PENDIENTE',label: '⏳ Pendiente / Generado' },
  { value: 'RECHAZADO',label: '✗ Rechazado' },
  { value: 'ANULADO',  label: '🗑️ Anulado' },
];

// Render de estado según origen del documento
const renderEstado = (doc) => {
  const { estado, origen } = doc;

  // CPE y Guías: estados de SUNAT
  if (origen === 'cpe' || origen === 'guia') {
    const meta = {
      PENDIENTE:  { color: 'info',    label: '⏳ Pendiente' },
      PROCESANDO: { color: 'info',    label: '⚙️ Procesando' },
      PREPARADO:  { color: 'warning', label: '📋 Preparado' },
      ENVIADO:    { color: 'warning', label: '📤 Enviado a SUNAT' },
      ACEPTADO:   { color: 'success', label: '✓ Aceptado' },
      RECHAZADO:  { color: 'error',   label: '✗ Rechazado' },
      ANULADO:    { color: 'error',   label: '🗑️ Anulado' },
    }[estado] || { color: 'default', label: estado };
    return (
      <Chip label={meta.label} color={meta.color} variant="outlined" size="small"
        sx={{ fontWeight: 'bold', fontSize: 11 }} />
    );
  }

  // Cotización
  if (origen === 'cotizacion') {
    if (estado === 'ANULADA' || estado === 'ANULADO') {
      return <Chip label="🗑️ Anulada" color="error" variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
    }
    return <Chip label="📋 Generada" color="info" variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
  }

  // Nota de Venta
  if (origen === 'nota_venta') {
    if (estado === 'ANULADA' || estado === 'ANULADO') {
      return <Chip label="🗑️ Anulada" color="error" variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
    }
    return <Chip label="📄 Emitida" color="info" variant="outlined" size="small" sx={{ fontWeight: 'bold', fontSize: 11 }} />;
  }

  // Fallback
  return <Chip label={estado || '-'} size="small" />;
};

const tipoLabel = {
  'Factura':           '#e3f2fd',
  'Boleta':            '#e8f5e9',
  'Nota Crédito':      '#fff3e0',
  'Nota Débito':       '#ffebee',
  'Cotización':        '#f3e5f5',
  'Nota de Venta':     '#e0f7fa',
  'Guía Remitente':    '#e1f5fe',
  'Guía Transportista':'#f1f8e9',
};

const hoy = new Date().toLocaleDateString('en-CA');
const primerDiaMes = new Date(new Date().setDate(1)).toISOString().slice(0, 10);

// ─── Columnas: anchos por defecto + definición (resize/ocultar como en Productos) ──
const VENTAS_COL_DEFAULTS = {
  fecha: 90, tipo: 150, numero: 130, cliente: 220, ruc: 110,
  total: 110, estado: 150, observaciones: 220, placa: 90,
};

const VENTAS_COLS_HIDDEN_KEY = 'centro-ventas-cols-hidden';

const VENTAS_COLS = [
  {
    key: 'fecha', label: 'Fecha',
    render: doc => (
      <Box sx={{ lineHeight: 1.3 }}>
        <div>{doc.fecha?.substring(0, 10)}</div>
        {doc.hora && <div style={{ fontSize: 11, color: '#888' }}>{doc.hora}</div>}
      </Box>
    ),
  },
  {
    key: 'tipo', label: 'Tipo',
    render: doc => (
      <Chip label={doc.tipo_doc} size="small"
        sx={{ bgcolor: tipoLabel[doc.tipo_doc] || '#f5f5f5', fontWeight: 600, fontSize: 11 }} />
    ),
  },
  {
    key: 'numero', label: 'Número',
    render: doc => {
      const ref = doc.referencia_comprobante || doc.origen_referencia;
      return (
        <Box sx={{ lineHeight: 1.3 }}>
          <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{doc.numero_doc}</div>
          {ref && (
            <Tooltip title={ref}>
              <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                ↳ {ref}
              </div>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    key: 'cliente', label: 'Cliente',
    render: doc => <Tooltip title={doc.cliente || ''}><span>{doc.cliente || '-'}</span></Tooltip>,
  },
  {
    key: 'ruc', label: 'RUC/DNI',
    render: doc => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{doc.ruc_cliente || '-'}</span>,
  },
  {
    key: 'total', label: 'Total', align: 'right',
    render: doc => (
      <span style={{ fontWeight: 600 }}>
        {doc.origen !== 'guia' && doc.total > 0
          ? `${doc.moneda === 'USD' ? '$' : 'S/'} ${parseFloat(doc.total).toFixed(2)}`
          : '-'}
      </span>
    ),
  },
  {
    key: 'estado', label: 'Estado',
    render: doc => renderEstado(doc),
  },
  {
    key: 'observaciones', label: 'Observaciones',
    render: doc => <Tooltip title={doc.observaciones || ''}><span>{doc.observaciones || '-'}</span></Tooltip>,
  },
  {
    key: 'placa', label: 'Placa',
    render: doc => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{doc.placa || '-'}</span>,
  },
];

// ─── Componente principal ─────────────────────────────────────────────────────

const AdminVentasPage = () => {
  const [filtros, setFiltros] = useState({
    tipos:      [],
    estados:    [],
    desde:      primerDiaMes,
    hasta:      hoy,
    cliente:    '',
    numero:     '',
    sucursalId: '',
    sucursalObj: null,
  });
  const [sucursales, setSucursales] = useState([]);
  const [showFiltros, setShowFiltros] = useState(true);
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const saved = localStorage.getItem(VENTAS_COLS_HIDDEN_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });
  const [colMenu, setColMenu] = useState(null);
  const { widths: colW, startResize } = useResizableCols('centro-ventas-cols', VENTAS_COL_DEFAULTS);
  const visibleVentasCols = VENTAS_COLS.filter(c => !hiddenCols.includes(c.key));

  const persistHiddenCols = (next) => {
    setHiddenCols(next);
    try { localStorage.setItem(VENTAS_COLS_HIDDEN_KEY, JSON.stringify(next)); } catch {}
  };
  const handleColHeaderContextMenu = (e) => {
    e.preventDefault();
    setColMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
  };
  const toggleVentasCol = (key) => {
    const isHidden = hiddenCols.includes(key);
    if (!isHidden && VENTAS_COLS.length - hiddenCols.length <= 1) return;
    persistHiddenCols(isHidden ? hiddenCols.filter(k => k !== key) : [...hiddenCols, key]);
  };
  const mostrarTodasVentasCols = () => {
    persistHiddenCols([]);
    setColMenu(null);
  };

  useEffect(() => {
    axios.get('/api/sucursales/mis-sucursales').then(r => setSucursales(r.data || [])).catch(() => {});
  }, []);

  const buildParams = useCallback((p = page) => {
    const params = { page: p, limit: 50 };
    if (filtros.tipos.length === 1) params.tipo = filtros.tipos[0];
    if (filtros.estados.length === 1) params.estado = filtros.estados[0];
    if (filtros.desde) params.desde = filtros.desde;
    if (filtros.hasta) params.hasta = filtros.hasta;
    if (filtros.cliente) params.cliente = filtros.cliente;
    if (filtros.numero) params.numero = filtros.numero;
    if (filtros.sucursalId) params.sucursalId = filtros.sucursalId;
    return params;
  }, [filtros, page]);

  const fetchDocs = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = buildParams(p);
      // Si hay múltiples tipos seleccionados, hacemos varias requests y unimos
      if (filtros.tipos.length > 1) {
        const results = await Promise.all(
          filtros.tipos.map(t => axios.get('/api/ventas/documentos', { params: { ...params, tipo: t } }))
        );
        let allDocs = results.flatMap(r => r.data.data || []);
        if (filtros.estados.length > 0) {
          allDocs = allDocs.filter(d => filtros.estados.includes(d.estado));
        }
        allDocs.sort((a, b) => (b.fecha > a.fecha ? 1 : b.fecha < a.fecha ? -1 : b.id - a.id));
        setDocs(allDocs);
        setTotal(results.reduce((s, r) => s + (r.data.total || 0), 0));
      } else {
        // Si hay múltiples estados, filtramos en frontend (los estados son pocos)
        const res = await axios.get('/api/ventas/documentos', { params });
        let data = res.data.data || [];
        if (filtros.estados.length > 1) {
          data = data.filter(d => filtros.estados.includes(d.estado));
        }
        setDocs(data);
        setTotal(res.data.total || 0);
      }
    } catch (e) {
      setDocs([]);
      console.error(e);
    }
    setLoading(false);
  }, [filtros, page, buildParams]);

  useEffect(() => { fetchDocs(page); }, [fetchDocs, page]);

  const handleTipos = (e) => {
    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    setFiltros(prev => ({ ...prev, tipos: val }));
    setPage(1);
  };

  const handleEstados = (e) => {
    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
    setFiltros(prev => ({ ...prev, estados: val }));
    setPage(1);
  };

  const handleFiltro = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const limpiarFiltros = () => {
    setFiltros({ tipos: [], estados: [], desde: primerDiaMes, hasta: hoy, cliente: '', numero: '', sucursalId: '', sucursalObj: null });
    setPage(1);
  };

  // ─ Archivos ──────────────────────────────────────────────────────────────

  const abrirArchivo = async (doc, tipoArchivo) => {
    if (tipoArchivo === 'pdf') {
      const tk = localStorage.getItem('token') || '';
      // _ts revienta cualquier caché del navegador — sin esto, un PDF pedido una vez
      // puede seguir sirviéndose desde caché aunque el documento haya cambiado en el servidor.
      const _ts = Date.now();
      if (doc.origen === 'cpe') {
        window.open(`/api/reporte-pdf/comprobante/${doc.id}?formato=A4&mode=inline&_ts=${_ts}&token=${tk}`, '_blank');
      } else if (doc.origen === 'guia') {
        window.open(`/api/reporte-pdf/guia/${doc.id}?formato=A4&mode=inline&_ts=${_ts}&token=${tk}`, '_blank');
      } else if (doc.origen === 'cotizacion') {
        window.open(`/api/reporte-pdf/cotizacion/${doc.id}?formato=A4&mode=inline&_ts=${_ts}&token=${tk}`, '_blank');
      } else if (doc.origen === 'nota_venta') {
        window.open(`/api/reporte-pdf/nota-venta/${doc.id}?formato=A4&mode=inline&_ts=${_ts}&token=${tk}`, '_blank');
      }
      return;
    }
    try {
      const res = await axios.get(`/api/ventas/doc-url/${doc.origen}/${doc.id}/${tipoArchivo}`);
      const url = res.data?.url;
      if (url) window.open(url, '_blank');
      else alert(res.data?.mensaje || 'Archivo no disponible');
    } catch {
      if (doc.origen === 'cpe') window.open(`/api/comprobante/${doc.id}/archivo-cpe/${tipoArchivo}`, '_blank');
      else if (doc.origen === 'guia') window.open(`/api/guia-remision/${doc.id}/archivo-cpe/${tipoArchivo}`, '_blank');
      else alert('No disponible');
    }
  };

  const tieneCpe = (doc) => doc.origen === 'cpe' || doc.origen === 'guia';

  // ─ Excel ─────────────────────────────────────────────────────────────────

  const exportarExcel = async () => {
    setExportando(true);
    try {
      // Fetch todos (sin paginación) con los filtros actuales
      let allDocs = [];
      if (filtros.tipos.length > 1) {
        const params = buildParams(1);
        const results = await Promise.all(
          filtros.tipos.map(t => axios.get('/api/ventas/documentos', { params: { ...params, limit: 5000, page: 1, tipo: t } }))
        );
        allDocs = results.flatMap(r => r.data.data || []);
        allDocs.sort((a, b) => (b.fecha > a.fecha ? 1 : b.fecha < a.fecha ? -1 : b.id - a.id));
      } else {
        const params = buildParams(1);
        const res = await axios.get('/api/ventas/documentos', { params: { ...params, limit: 5000, page: 1 } });
        allDocs = res.data.data || [];
        if (filtros.estados.length > 1) allDocs = allDocs.filter(d => filtros.estados.includes(d.estado));
      }

      const tiposFiltroLabel = filtros.tipos.length
        ? filtros.tipos.map(t => TIPO_OPTIONS.find(o => o.value === t)?.label || t).join(', ')
        : 'Todos';
      const estadosFiltroLabel = filtros.estados.length
        ? filtros.estados.join(', ')
        : 'Todos';
      const filtroTexto = `Tipos: ${tiposFiltroLabel} | Estados: ${estadosFiltroLabel} | ${filtros.desde || '---'} a ${filtros.hasta || '---'}`;

      // ── Hoja adicional: cabecera + detalle (producto, cantidad, precio, total) ──
      const idsPorOrigen = { cpe: [], cotizacion: [], nota_venta: [], guia: [] };
      allDocs.forEach(d => {
        const clave = d.origen === 'guia' ? 'guia' : d.origen;
        if (idsPorOrigen[clave]) idsPorOrigen[clave].push(d.id);
      });

      let detallePorDoc = {};
      try {
        const resDet = await axios.post('/api/ventas/detalle-lote', idsPorOrigen);
        (resDet.data || []).forEach(linea => {
          const clave = `${linea.origen}-${linea.doc_id}`;
          if (!detallePorDoc[clave]) detallePorDoc[clave] = [];
          detallePorDoc[clave].push(linea);
        });
      } catch (e) {
        console.error('No se pudo obtener el detalle para la hoja Excel:', e);
      }

      const grupos = allDocs.map(d => ({
        cabecera: {
          fecha: d.fecha?.substring(0, 10),
          fecha_vencimiento: d.fecha_vencimiento?.substring(0, 10) || '',
          tipo_doc: d.tipo_doc,
          condicion_pago: d.condicion_pago || '',
          ruc_cliente: d.ruc_cliente || '',
          cliente: d.cliente,
          placa: d.placa || '',
          guia_ref: d.guia_ref || '',
          nv_ref: d.nv_ref || '',
          cotizacion_ref: d.cotizacion_ref || '',
          numero_doc: d.numero_doc,
          moneda: d.moneda || '',
          tipo_cambio: d.tipo_cambio ? parseFloat(d.tipo_cambio) : '',
          total: d.origen !== 'guia' && d.total > 0 ? parseFloat(d.total) : 0,
          estado: d.estado,
          vendedor: d.vendedor || '',
          hora: d.hora || '',
          observaciones: d.observaciones || '',
        },
        detalle: (detallePorDoc[`${d.origen}-${d.id}`] || []).map(l => ({
          producto_codigo: l.producto_codigo || '',
          producto_nombre: l.producto_nombre || '',
          cantidad: parseFloat(l.cantidad) || 0,
          precio_unitario: parseFloat(l.precio_unitario) || 0,
          total: parseFloat(l.total) || 0,
        })),
      }));

      const columnasPrincipales = [
        { key: 'fecha',       label: 'Fecha',        width: 14 },
        { key: 'tipo_doc',    label: 'Tipo',         width: 20 },
        { key: 'numero_doc',  label: 'Número',       width: 18 },
        { key: 'cliente',     label: 'Cliente',      width: 40 },
        { key: 'ruc_cliente', label: 'RUC/DNI',      width: 14 },
        { key: 'total',       label: 'Total',        width: 14, sumTotal: true },
        { key: 'moneda',      label: 'Moneda',       width: 10 },
        { key: 'estado',      label: 'Estado',       width: 16 },
        { key: 'observaciones', label: 'Observaciones', width: 30 },
        { key: 'placa',       label: 'Placa',        width: 12 },
      ];
      const filasPrincipales = allDocs.map(d => {
        const estUp = String(d.estado || '').toUpperCase();
        const anulado = ['ANULADA', 'ANULADO', 'RECHAZADA', 'RECHAZADO'].includes(estUp);
        return {
          ...d,
          fecha: d.fecha ? String(d.fecha).substring(0, 10) : '',
          total: anulado ? 0 : (d.origen !== 'guia' && d.total > 0 ? parseFloat(d.total) : 0),
        };
      });

      // ── Hoja 4: mismas filas, agrupadas por condición de pago (Contado/Yape/
      // Transferencia/Crédito X días, tal cual está en cada documento) — las
      // guías no tienen condición de pago propia, se excluyen de esta hoja.
      const gruposPorPago = {};
      filasPrincipales.forEach(d => {
        if (d.origen === 'guia') return;
        const clave = (d.condicion_pago || '').trim() || 'Sin condición de pago';
        if (!gruposPorPago[clave]) gruposPorPago[clave] = [];
        gruposPorPago[clave].push(d);
      });
      const gruposPorPagoOrdenados = Object.keys(gruposPorPago).sort().map(titulo => ({
        titulo,
        rows: gruposPorPago[titulo],
      }));

      // ── Hoja 5: Trazabilidad de Documentos — una fila por cadena real de venta
      // (Factura→Guía→NV→Cotización), sin repetir ningún documento. Respeta los
      // mismos filtros (tipos/fechas/cliente/estado) que el resto del export.
      let filasTrazabilidad = [];
      try {
        const paramsCadena = buildParams(1);
        delete paramsCadena.tipo;
        if (filtros.tipos.length) paramsCadena.tipos = filtros.tipos.join(',');
        if (filtros.estados.length === 1) paramsCadena.estado = filtros.estados[0];
        const resCadena = await axios.get('/api/ventas/cadena-documentos', { params: paramsCadena });
        filasTrazabilidad = resCadena.data || [];
        if (filtros.estados.length > 1) {
          filasTrazabilidad = filasTrazabilidad.filter(f => filtros.estados.includes(f.estado));
        }
      } catch (e) {
        console.error('No se pudo obtener la hoja de trazabilidad:', e);
      }

      // Mapa origen-id → fila donde empieza ese documento en "Movimientos
      // Detalle" (esa hoja es plana: 1 fila por línea, documento tras
      // documento, en el mismo orden que `grupos`/`allDocs`). Permite que la
      // columna "Ver Detalle" de Trazabilidad salte directo ahí con un clic.
      const filaInicioPorDoc = new Map();
      {
        let fila = 2; // la fila 1 es el encabezado
        allDocs.forEach((d, i) => {
          filaInicioPorDoc.set(`${d.origen}-${d.id}`, fila);
          fila += grupos[i].detalle.length || 1;
        });
      }
      filasTrazabilidad = filasTrazabilidad.map(f => {
        const filaInicio = filaInicioPorDoc.get(`${f.raiz_origen}-${f.raiz_id}`);
        return {
          ...f,
          ver_detalle: filaInicio
            ? { text: 'Ver detalle →', target: `#'Movimientos Detalle'!A${filaInicio}` }
            : { text: '—' },
        };
      });

      const columnasTrazabilidad = [
        { key: 'fecha',             label: 'Fecha',             width: 14 },
        { key: 'hora',              label: 'Hora',              width: 10 },
        { key: 'factura_tipo',      label: 'Tipo Comprobante',  width: 16 },
        { key: 'factura_numero',    label: 'Factura/Boleta',    width: 16 },
        { key: 'guia_numero',       label: 'Guía',               width: 16 },
        { key: 'nv_numero',         label: 'Nota de Venta',      width: 16 },
        { key: 'cotizacion_numero', label: 'Cotización',         width: 16 },
        { key: 'cliente',           label: 'Cliente',            width: 40 },
        { key: 'ruc_cliente',       label: 'RUC/DNI',            width: 14 },
        { key: 'placa',             label: 'Placa',              width: 12 },
        { key: 'total',             label: 'Total',              width: 14, sumTotal: true },
        { key: 'moneda',            label: 'Moneda',             width: 10 },
        { key: 'estado',            label: 'Estado',             width: 16, isEstado: true },
        { key: 'vendedor',          label: 'Vendedor',           width: 22 },
        { key: 'observaciones',     label: 'Observaciones',      width: 30 },
        { key: 'ver_detalle',       label: 'Ver Detalle',        width: 16, isLink: true },
      ];

      await exportGenericWorkbook({
        titulo: 'CENTRO DE VENTAS — DOCUMENTOS',
        sheetName: 'CentroVentas',
        tableName: 'tblCentroVentas',
        fileName: 'CentroVentas',
        skipDataSheet: true,
        searchText: filtroTexto,
        usuario: (() => { try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.username || ''; } catch { return ''; } })(),
        columns: columnasPrincipales,
        rows: filasPrincipales,
        agrupadoPorCampo: {
          sheetName: 'Por Condición de Pago',
          columns: columnasPrincipales,
          grupos: gruposPorPagoOrdenados,
        },
        detalleAgrupado: {
          sheetName: 'Movimientos Detalle',
          cabeceraCols: [
            { key: 'fecha',             label: 'Fecha Emisión',      width: 14 },
            { key: 'fecha_vencimiento', label: 'Fecha Vencimiento',  width: 14 },
            { key: 'tipo_doc',          label: 'Tipo',               width: 20 },
            { key: 'condicion_pago',    label: 'Forma de Pago',      width: 18 },
            { key: 'ruc_cliente',       label: 'RUC/DNI',            width: 14 },
            { key: 'cliente',           label: 'Razón Social',       width: 40 },
            { key: 'placa',             label: 'Placa',              width: 12 },
            { key: 'guia_ref',          label: 'Guía',               width: 16 },
            { key: 'nv_ref',            label: 'Nota de Venta',      width: 16 },
            { key: 'cotizacion_ref',    label: 'Cotización',         width: 16 },
            { key: 'numero_doc',        label: 'Número',             width: 18 },
            { key: 'moneda',            label: 'Moneda',             width: 10 },
            { key: 'tipo_cambio',       label: 'Tipo de Cambio',     width: 12, isNumeric: true },
            { key: 'total',             label: 'Total Doc.',         width: 14, isNumeric: true },
            { key: 'estado',            label: 'Estado',             width: 16, isEstado: true },
            { key: 'vendedor',          label: 'Vendedor',           width: 22 },
            { key: 'hora',              label: 'Hora',               width: 10 },
            { key: 'observaciones',     label: 'Observaciones',      width: 30 },
          ],
          detalleCols: [
            { key: 'producto_codigo',  label: 'Código',          width: 16 },
            { key: 'producto_nombre',  label: 'Producto',        width: 45 },
            { key: 'cantidad',         label: 'Cantidad',        width: 12, isNumeric: true },
            { key: 'precio_unitario',  label: 'Precio Unitario', width: 14, isNumeric: true },
            { key: 'total',            label: 'Total Línea',     width: 14, isNumeric: true },
          ],
          grupos,
        },
        flatSheet: {
          sheetName: 'Trazabilidad de Documentos',
          columns: columnasTrazabilidad,
          rows: filasTrazabilidad,
        },
      });
    } catch (e) {
      alert('Error al exportar: ' + e.message);
    }
    setExportando(false);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  const tiposLabel = filtros.tipos.length
    ? filtros.tipos.map(v => TIPO_OPTIONS.find(o => o.value === v)?.label || v).join(', ')
    : 'Todos los tipos';

  const estadosLabel = filtros.estados.length
    ? filtros.estados.join(', ')
    : 'Todos los estados';

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Receipt color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Centro de Ventas</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {total} documentos
        </Typography>
        <Button size="small" variant="outlined" color="success"
          startIcon={exportando ? <CircularProgress size={14} color="inherit" /> : <TableChart />}
          onClick={exportarExcel} disabled={exportando || loading}>
          Excel
        </Button>
        <Button size="small" variant="outlined"
          startIcon={<FilterList />}
          onClick={() => setShowFiltros(v => !v)}>
          {showFiltros ? 'Ocultar filtros' : 'Filtros'}
        </Button>
        <IconButton onClick={() => fetchDocs(page)} disabled={loading} size="small">
          <Refresh />
        </IconButton>
      </Box>

      {/* Filtros */}
      <Collapse in={showFiltros}>
        <Paper sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>

            {/* Sucursal — igual que Kardex */}
            <Autocomplete size="small" sx={{ minWidth: 170, flex: '1 1 170px' }}
              options={sucursales}
              getOptionLabel={o => o.nombre || ''}
              value={filtros.sucursalObj}
              onChange={(_, v) => { setFiltros(f => ({ ...f, sucursalId: v?.id || '', sucursalObj: v })); setPage(1); }}
              renderInput={p => <TextField {...p} label="Sucursal" />}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
            />

            {/* Tipo Documento — tamaño Producto Kardex */}
            <FormControl size="small" sx={{ minWidth: 240, flex: '2 1 240px' }}>
              <InputLabel id="tipo-label">Tipo documento</InputLabel>
              <Select
                labelId="tipo-label"
                multiple
                value={filtros.tipos}
                onChange={handleTipos}
                input={<OutlinedInput label="Tipo documento" />}
                renderValue={() => tiposLabel}
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}>
                {TIPO_OPTIONS.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Checkbox checked={filtros.tipos.includes(t.value)} size="small" />
                    <ListItemText primary={t.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Estado — tamaño Producto Kardex */}
            <FormControl size="small" sx={{ minWidth: 240, flex: '2 1 240px' }}>
              <InputLabel id="estado-label">Estado</InputLabel>
              <Select
                labelId="estado-label"
                multiple
                value={filtros.estados}
                onChange={handleEstados}
                input={<OutlinedInput label="Estado" />}
                renderValue={() => estadosLabel}
                MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}>
                {ESTADO_OPTIONS.map(e => (
                  <MenuItem key={e.value} value={e.value}>
                    <Checkbox checked={filtros.estados.includes(e.value)} size="small" />
                    <ListItemText primary={e.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Fechas — igual que Kardex */}
            <TextField label="Desde" type="date" size="small" sx={{ minWidth: 135 }}
              InputLabelProps={{ shrink: true }}
              value={filtros.desde} onChange={e => handleFiltro('desde', e.target.value)} />
            <TextField label="Hasta" type="date" size="small" sx={{ minWidth: 135 }}
              InputLabelProps={{ shrink: true }}
              value={filtros.hasta} onChange={e => handleFiltro('hasta', e.target.value)} />

            {/* Cliente */}
            <TextField label="Cliente / RUC / DNI" size="small" sx={{ minWidth: 200, flex: '2 1 200px' }}
              value={filtros.cliente} onChange={e => handleFiltro('cliente', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchDocs(1)} />

            {/* N° doc */}
            <TextField label="N° doc" size="small" sx={{ minWidth: 110 }}
              value={filtros.numero} onChange={e => handleFiltro('numero', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchDocs(1)} />

            {/* Acciones */}
            <Button variant="contained" size="small"
              onClick={() => fetchDocs(1)}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Search />}
              disabled={loading}>
              Buscar
            </Button>
            <Tooltip title="Limpiar filtros">
              <IconButton onClick={limpiarFiltros} size="small"><Clear /></IconButton>
            </Tooltip>

          </Box>
        </Paper>
      </Collapse>

      {/* Tabla */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Clic derecho en el encabezado = mostrar/ocultar columnas · Arrastra el borde de una columna para redimensionar
      </Typography>
      <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: visibleVentasCols.reduce((s, c) => s + (colW[c.key] || 80), 0) + 140 }}>
          <colgroup>
            {visibleVentasCols.map(c => <col key={c.key} style={{ width: colW[c.key] }} />)}
            <col style={{ width: 140 }} />
          </colgroup>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }} onContextMenu={handleColHeaderContextMenu}>
              {visibleVentasCols.map(col => (
                <TableCell key={col.key} align={col.align || 'left'}
                  sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', position: 'relative', userSelect: 'none', cursor: 'context-menu' }}>
                  {col.label}
                  <ResizeHandle onMouseDown={e => startResize(col.key, e)} />
                </TableCell>
              ))}
              <TableCell align="left" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Archivos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={visibleVentasCols.length + 1} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleVentasCols.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No se encontraron documentos con los filtros seleccionados.
                </TableCell>
              </TableRow>
            ) : docs.map((doc, i) => (
              <TableRow key={`${doc.origen}-${doc.id}`} hover
                sx={{ bgcolor: i % 2 === 0 ? 'inherit' : 'action.hover' }}>
                {visibleVentasCols.map(col => (
                  <TableCell key={col.key} align={col.align || 'left'}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {col.render(doc)}
                  </TableCell>
                ))}
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Ver PDF">
                    <IconButton size="small" color="error" onClick={() => abrirArchivo(doc, 'pdf')}>
                      <PictureAsPdf fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {tieneCpe(doc) && (
                    <Tooltip title="Descargar XML">
                      <IconButton size="small" color="primary" onClick={() => abrirArchivo(doc, 'xml')}>
                        <Code fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {tieneCpe(doc) && (
                    <Tooltip title="Descargar CDR (respuesta SUNAT)">
                      <IconButton size="small" color="success" onClick={() => abrirArchivo(doc, 'cdr')}>
                        <Archive fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Menu
          open={!!colMenu}
          onClose={() => setColMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={colMenu ? { top: colMenu.mouseY, left: colMenu.mouseX } : undefined}
        >
          <MenuItem dense onClick={mostrarTodasVentasCols} disabled={hiddenCols.length === 0}>
            <ListItemText>Mostrar todas</ListItemText>
          </MenuItem>
          <Divider />
          {VENTAS_COLS.map(col => (
            <MenuItem key={col.key} dense onClick={() => toggleVentasCol(col.key)} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={!hiddenCols.includes(col.key)} sx={{ p: 0.5 }} />
              <ListItemText primary={col.label} />
            </MenuItem>
          ))}
        </Menu>
      </TableContainer>

      {/* Paginación */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Mostrando {docs.length} de {total} documentos
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <Typography variant="caption">Pág. {page}</Typography>
          <Button size="small" disabled={docs.length < 50} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminVentasPage;
